import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiDownload, FiRefreshCw, FiUser, FiMail, FiPhone, FiCalendar, FiFilter, FiX } from 'react-icons/fi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: calc(100vh - 80px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 28px;
    color: #333;
    margin: 0;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .stat-label {
    font-size: 13px;
    color: #999;
    margin-bottom: 8px;
  }
  
  .stat-value {
    font-size: 32px;
    font-weight: 700;
    color: #333;
  }
  
  .stat-source {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => props.primary ? '#3498db' : 'white'};
  color: ${props => props.primary ? 'white' : '#666'};
  border: 1px solid ${props => props.primary ? '#3498db' : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2980b9' : '#f8f9fa'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FilterBar = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  align-items: center;
  
  select, input {
    padding: 10px 15px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #3498db;
    }
  }
  
  select {
    min-width: 150px;
  }
  
  input[type="search"] {
    flex: 1;
    min-width: 200px;
  }
`;

const Table = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2.5fr 1fr 1.5fr 1fr 1fr 100px;
  gap: 15px;
  padding: 15px 20px;
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
  font-weight: 600;
  font-size: 13px;
  color: #666;
  text-transform: uppercase;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2.5fr 1fr 1.5fr 1fr 1fr 100px;
  gap: 15px;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  font-size: 14px;
  color: #333;
  
  &.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const SourceBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  
  &.party {
    background: #e3f2fd;
    color: #1976d2;
  }
  
  &.chat {
    background: #f3e5f5;
    color: #7b1fa2;
  }
  
  &.custom {
    background: #e8f5e9;
    color: #2e7d32;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  
  &.new {
    background: #fff3cd;
    color: #856404;
  }
  
  &.contacted {
    background: #cfe2ff;
    color: #084298;
  }
  
  &.qualified {
    background: #d1e7dd;
    color: #0a3622;
  }
  
  &.converted {
    background: #d1ecf1;
    color: #055160;
  }
  
  &.closed {
    background: #f8d7da;
    color: #721c24;
  }
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: #2980b9;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: white;
  margin-top: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  .page-info {
    font-size: 14px;
    color: #666;
  }
  
  .page-controls {
    display: flex;
    gap: 10px;
  }
  
  button {
    padding: 8px 16px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    
    &:hover:not(:disabled) {
      background: #f8f9fa;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    &.active {
      background: #3498db;
      color: white;
      border-color: #3498db;
    }
  }
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #999;
  
  svg {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.3;
  }
  
  p {
    font-size: 16px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    margin: 0 0 20px 0;
    color: #333;
  }
  
  .detail-section {
    margin-bottom: 20px;
    
    h3 {
      font-size: 16px;
      color: #666;
      margin-bottom: 10px;
      border-bottom: 1px solid #e0e0e0;
      padding-bottom: 8px;
    }
    
    .detail-grid {
      display: grid;
      grid-template-columns: 150px 1fr;
      gap: 12px;
      
      .label {
        font-weight: 600;
        color: #666;
      }
      
      .value {
        color: #333;
      }
    }
  }
`;

const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ source: '', status: '', search: '' });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [pagination.page, filters]);

  const fetchLeads = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      
      const response = await axios.get(
        `${API_URL}/api/leads?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setLeads(response.data.leads || []);
        setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        setStats(response.data.stats || { total: 0, bySource: { party: 0, chat: 0, custom: 0 } });
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const syncLeads = async () => {
    if (!window.confirm('Sync all leads from ticket purchases and chat conversations?')) return;
    
    setSyncing(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_URL}/api/leads/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert(response.data.message);
        fetchLeads();
      }
    } catch (error) {
      console.error('Error syncing leads:', error);
      alert('Failed to sync leads');
    } finally {
      setSyncing(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams(filters);
      
      window.open(
        `${API_URL}/api/leads/export/csv?${params}&token=${token}`,
        '_blank'
      );
    } catch (error) {
      console.error('Error exporting leads:', error);
      alert('Failed to export leads');
    }
  };

  const viewLeadDetails = async (leadId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_URL}/api/leads/${leadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSelectedLead(response.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
      alert('Failed to fetch lead details');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container>
      <Header>
        <h1>Leads Management</h1>
        <Actions>
          <Button onClick={syncLeads} disabled={syncing}>
            <FiRefreshCw className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync Leads'}
          </Button>
          <Button primary onClick={exportToCSV}>
            <FiDownload />
            Export CSV
          </Button>
        </Actions>
      </Header>

      {stats && (
        <Stats>
          <StatCard>
            <div className="stat-label">Total Leads</div>
            <div className="stat-value">{stats.total}</div>
          </StatCard>
          <StatCard>
            <div className="stat-label">Party Tickets</div>
            <div className="stat-value">{stats.bySource.party}</div>
            <div className="stat-source">From ticket purchases</div>
          </StatCard>
          <StatCard>
            <div className="stat-label">Chat Inquiries</div>
            <div className="stat-value">{stats.bySource.chat}</div>
            <div className="stat-source">From customer chats</div>
          </StatCard>
          <StatCard>
            <div className="stat-label">Custom Forms</div>
            <div className="stat-value">{stats.bySource.custom}</div>
            <div className="stat-source">From website forms</div>
          </StatCard>
        </Stats>
      )}

      <FilterBar>
        <FiFilter />
        <select
          value={filters.source}
          onChange={(e) => setFilters({ ...filters, source: e.target.value })}
        >
          <option value="">All Sources</option>
          <option value="Party">Party</option>
          <option value="Chat">Chat</option>
          <option value="Custom">Custom</option>
        </select>
        
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="qualified">Qualified</option>
          <option value="converted">Converted</option>
          <option value="closed">Closed</option>
        </select>
        
        <input
          type="search"
          placeholder="Search by name, email, or phone..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
      </FilterBar>

      <Table>
        <TableHeader>
          <div>Name</div>
          <div>Contact</div>
          <div>Source</div>
          <div>Event Type</div>
          <div>Status</div>
          <div>Added</div>
          <div>Actions</div>
        </TableHeader>
        
        {loading ? (
          <EmptyState>
            <FiRefreshCw className="spinning" />
            <p>Loading leads...</p>
          </EmptyState>
        ) : leads.length === 0 ? (
          <EmptyState>
            <FiUser />
            <p>No leads found</p>
          </EmptyState>
        ) : (
          leads.map(lead => (
            <TableRow key={lead._id}>
              <Cell className="truncate">{lead.name}</Cell>
              <Cell className="truncate">
                {lead.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: lead.phone ? '4px' : '0' }}>
                    <FiMail size={14} style={{ color: '#666', flexShrink: 0 }} />
                    <span>{lead.email}</span>
                  </div>
                )}
                {lead.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiPhone size={14} style={{ color: '#666', flexShrink: 0 }} />
                    <span>{lead.phone}</span>
                  </div>
                )}
                {!lead.email && !lead.phone && '-'}
              </Cell>
              <Cell>
                <SourceBadge className={lead.source.toLowerCase()}>
                  {lead.source}
                </SourceBadge>
              </Cell>
              <Cell className="truncate">{lead.eventType || '-'}</Cell>
              <Cell>
                <StatusBadge className={lead.status}>
                  {lead.status}
                </StatusBadge>
              </Cell>
              <Cell>{formatDate(lead.addedDate)}</Cell>
              <Cell>
                <ViewButton onClick={() => viewLeadDetails(lead._id)}>
                  View
                </ViewButton>
              </Cell>
            </TableRow>
          ))
        )}
      </Table>

      {pagination && pagination.pages > 1 && (
        <Pagination>
          <div className="page-info">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} leads
          </div>
          <div className="page-controls">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            {[...Array(pagination.pages)].map((_, i) => (
              <button
                key={i + 1}
                className={pagination.page === i + 1 ? 'active' : ''}
                onClick={() => setPagination({ ...pagination, page: i + 1 })}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page === pagination.pages}
            >
              Next
            </button>
          </div>
        </Pagination>
      )}

      <Modal show={showModal}>
        <ModalContent>
          {selectedLead && (
            <>
              <h2>Lead Details</h2>
              
              <div className="detail-section">
                <h3>Contact Information</h3>
                <div className="detail-grid">
                  <div className="label">Name:</div>
                  <div className="value">{selectedLead.lead.name}</div>
                  
                  <div className="label">Email:</div>
                  <div className="value">{selectedLead.lead.email || '-'}</div>
                  
                  <div className="label">Phone:</div>
                  <div className="value">{selectedLead.lead.phone || '-'}</div>
                </div>
              </div>
              
              <div className="detail-section">
                <h3>Source Information</h3>
                <div className="detail-grid">
                  <div className="label">Source:</div>
                  <div className="value">
                    <SourceBadge className={selectedLead.lead.source.toLowerCase()}>
                      {selectedLead.lead.source}
                    </SourceBadge>
                  </div>
                  
                  <div className="label">Detail:</div>
                  <div className="value">{selectedLead.lead.sourceDetail}</div>
                  
                  <div className="label">Event Type:</div>
                  <div className="value">{selectedLead.lead.eventType || '-'}</div>
                  
                  <div className="label">Added Date:</div>
                  <div className="value">{formatDate(selectedLead.lead.addedDate)}</div>
                </div>
              </div>
              
              {selectedLead.lead.customFields && Object.keys(selectedLead.lead.customFields).length > 0 && (
                <div className="detail-section">
                  <h3>Custom Fields</h3>
                  <div className="detail-grid">
                    {Object.entries(selectedLead.lead.customFields).map(([key, value]) => (
                      <React.Fragment key={key}>
                        <div className="label">{key.replace(/_/g, ' ')}:</div>
                        <div className="value">{value}</div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
              
              <Button onClick={() => setShowModal(false)} style={{ marginTop: '20px' }}>
                <FiX />
                Close
              </Button>
            </>
          )}
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default LeadsManagement;
